import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SETTINGS_KEYS } from '../../config/environment'
import { useCorrespondentsCache } from '../../hooks/useCorrespondentsCache'
import { useDocumentTypesCache } from '../../hooks/useDocumentTypesCache'
import { useSetting } from '../../hooks/useSettings'
import { useStoragePathsCache } from '../../hooks/useStoragePathsCache'
import { useThumbnail } from '../../hooks/useThumbnail'
import { useWorkflowStatusesCache } from '../../hooks/useWorkflowStatusesCache'
import { DisplayFieldEnum, documentListViewService } from '../../services/document-list-view.service'
import { documentService } from '../../services/document.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'
import { toastService } from '../../services/toast.service'
import { Tag as TagType } from '../../types'
import { formatCustomFieldDisplayValue } from '../../utils/customFieldDisplay'
import { formatDate } from '../../utils/dateFormatter'
import { getDocumentOwnerDisplayName } from '../../utils/documentOwnerDisplay'
import type { DocumentCardUsersCacheEntry } from './DocumentCardSmall'
import ConfirmDialog from '../common/ConfirmDialog'
import PreviewPopup from '../common/PreviewPopup'
import Tag from '../common/Tag'
import './DocumentCardLarge.scss'

interface DocumentCardLargeProps {
  selected: boolean
  document: any
  displayFields: DisplayFieldEnum[]
  /** Populated once by the parent; cards must not fetch users themselves */
  usersCache: DocumentCardUsersCacheEntry[]
  usersCacheLoaded: boolean
  onToggleSelected: (document: any, event: React.MouseEvent) => void
  onDoubleClickDocument: (document: any) => void
  onClickTag: (tagId: number) => void
  onClickMoreLike: (documentId: number) => void
  onClickCorrespondent?: (correspondentId: number) => void
  onClickDocumentType?: (documentTypeId: number) => void
  onClickWorkflowStatus?: (workflowStatusId: number) => void
  onClickStoragePath?: (storagePathId: number) => void
}

const DocumentCardLarge: React.FC<DocumentCardLargeProps> = ({
  selected,
  document,
  displayFields,
  usersCache,
  usersCacheLoaded,
  onToggleSelected,
  onDoubleClickDocument,
  onClickTag,
  onClickMoreLike,
  onClickCorrespondent,
  onClickDocumentType,
  onClickWorkflowStatus,
  onClickStoragePath,
}) => {
  const { t } = useTranslation()
  const [show] = useState(true)
  const [currentUserId, setCurrentUserId] = useState<number | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false)
  const { blobUrl: thumbBlobUrl } = useThumbnail(document?.id)
  const showVersionWatermark = useSetting<boolean>(SETTINGS_KEYS.DOCUMENT_LIST_SHOW_VERSION_WATERMARK, true)
  const showStatusWatermark = useSetting<boolean>(SETTINGS_KEYS.DOCUMENT_LIST_SHOW_STATUS_WATERMARK, true)
  const { correspondents: correspondentsCache, loaded: correspondentsCacheLoaded } = useCorrespondentsCache()
  const { documentTypes: documentTypesCache, loaded: documentTypesCacheLoaded } = useDocumentTypesCache()
  const { storagePaths: storagePathsCache, loaded: storagePathsCacheLoaded } = useStoragePathsCache()
  const { workflowStatuses: workflowStatusesCache, loaded: workflowStatusesCacheLoaded } = useWorkflowStatusesCache()

  useEffect(() => {
    const uiSettings = settingsService.get('user')
    if (uiSettings?.id) {
      setCurrentUserId(uiSettings.id)
    }
  }, [])

  const getCorrespondentName = (correspondent: number | any | null | undefined): string => {
    if (!correspondent) return ''

    if (typeof correspondent === 'object' && correspondent.name) {
      return correspondent.name
    }

    if (document.correspondent_display?.name) {
      return document.correspondent_display.name
    }

    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.Correspondent)) {
      return t('private')
    }

    const correspondentId = typeof correspondent === 'number' ? correspondent : null
    if (!correspondentId) return ''

    if (!correspondentsCacheLoaded || correspondentsCache.length === 0) {
      return ''
    }

    const corr = correspondentsCache.find((c: any) => c.id === correspondentId)
    if (!corr) {
      return t('private')
    }

    return corr.name || t('private')
  }

  const getDocumentTypeName = (documentType: number | any | null | undefined): string => {
    if (!documentType) return ''

    if (typeof documentType === 'object' && documentType.name) {
      return documentType.name
    }

    if (document.document_type_display?.name) {
      return document.document_type_display.name
    }

    const documentTypeId = typeof documentType === 'number' ? documentType : null
    if (!documentTypeId) return ''

    if (!documentTypesCacheLoaded || documentTypesCache.length === 0) {
      return ''
    }

    const dt = documentTypesCache.find((d: any) => d.id === documentTypeId)
    return dt?.name || ''
  }

  const getWorkflowStatusName = (workflowStatus: number | any | null | undefined): string => {
    if (!workflowStatus) return ''
    if (typeof workflowStatus === 'object' && workflowStatus.name) return workflowStatus.name
    const id = typeof workflowStatus === 'number' ? workflowStatus : null
    if (!id || !workflowStatusesCacheLoaded) return ''
    const ws = workflowStatusesCache.find((s: any) => s.id === id)
    return ws?.name || ''
  }

  const getWorkflowStatusStyle = (workflowStatus: number | any | null | undefined): { backgroundColor: string; color: string } => {
    if (!workflowStatus) return { backgroundColor: '#6c757d', color: '#fff' }
    if (typeof workflowStatus === 'object' && (workflowStatus.color || workflowStatus.text_color)) {
      return {
        backgroundColor: (workflowStatus as any).color || '#6c757d',
        color: (workflowStatus as any).text_color || '#fff',
      }
    }
    const id = typeof workflowStatus === 'number' ? workflowStatus : null
    if (!id || !workflowStatusesCacheLoaded) return { backgroundColor: '#6c757d', color: '#fff' }
    const ws = workflowStatusesCache.find((s: any) => s.id === id)
    return {
      backgroundColor: ws?.color || '#6c757d',
      color: ws?.text_color || '#fff',
    }
  }

  const getStoragePathName = (storagePath: number | any | null | undefined): string => {
    if (!storagePath) return ''

    if (typeof storagePath === 'object' && storagePath.name) {
      return storagePath.name
    }

    if (document.storage_path_display?.name) {
      return document.storage_path_display.name
    }

    const storagePathId = typeof storagePath === 'number' ? storagePath : null
    if (!storagePathId) return ''

    if (!storagePathsCacheLoaded || storagePathsCache.length === 0) {
      return ''
    }

    const sp = storagePathsCache.find((s: any) => s.id === storagePathId)
    return sp?.name || ''
  }

  const getTagDisplayById = (tagId: number): TagType | undefined => {
    const tagsDisplay = document.tags_display || []
    const found = tagsDisplay.find((tag: any) => tag.id === tagId)
    if (!found) return undefined
    return {
      id: found.id,
      name: found.name,
      color: found.color || '#6c757d',
      text_color: found.text_color || '#ffffff',
      parent: found.parent ?? null,
      is_inbox_tag: false,
      matching_algorithm: 1,
      match: '',
      is_insensitive: true,
    }
  }

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    try {
      await documentService.downloadDocumentFile(document.id, false, document.title || document.original_file_name)
    } catch (error) {}
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = async (permanentDelete?: boolean) => {
    if (isDeleteSubmitting) return
    try {
      setIsDeleteSubmitting(true)
      if (permanentDelete) {
        await documentService.bulkEditOperation([document.id], 'delete', { permanent: true })
        toastService.showSuccess(
          t('document_permanently_deleted', { title: document.title || document.original_file_name || 'Untitled' }) ||
            t('document_deleted', { title: document.title || document.original_file_name || 'Untitled' })
        )
      } else {
        await documentService.deleteDocument(document.id)
        toastService.showSuccess(
          t('document_moved_to_trash', { title: document.title || document.original_file_name || 'Untitled' }) ||
            t('document_deleted', { title: document.title || document.original_file_name || 'Untitled' })
        )
      }
      documentListViewService.reload()
      setShowDeleteDialog(false)
    } catch (error: any) {
      const message =
        error?.response?.status === 403
          ? t('error_deleting_document_no_permission')
          : t('error_deleting_document') || 'Greška pri brisanju dokumenta'
      toastService.showError(message)
      setShowDeleteDialog(false)
    } finally {
      setIsDeleteSubmitting(false)
    }
  }

  const canDelete = permissionsService.currentUserCan(PermissionAction.Delete, PermissionType.Document)

  const getIsThumbInverted = () => {
    const darkModeEnabled = settingsService.get(SETTINGS_KEYS.DARK_MODE_ENABLED)
    const darkModeUseSystem = settingsService.get(SETTINGS_KEYS.DARK_MODE_USE_SYSTEM)
    const thumbInverted = settingsService.get(SETTINGS_KEYS.DARK_MODE_THUMB_INVERTED)

    let isDarkMode = false

    if (darkModeUseSystem) {
      isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
    } else {
      isDarkMode = darkModeEnabled
    }

    return isDarkMode && thumbInverted
  }

  return (
    <div
      className={`card document-card-large mb-3 shadow-sm bg-light placeholder-glow fade ${show ? 'show' : ''} ${selected ? 'card-selected' : ''} ${document ? 'document-card' : ''}`}
    >
      <div className="row g-0">
        <div
          className="col-md-2 doc-img-container rounded-start"
          onClick={(e) => onToggleSelected(document, e)}
          onDoubleClick={() => onDoubleClickDocument(document)}
        >
          {document ? (
            <>
              {thumbBlobUrl ? (
                <img
                  src={thumbBlobUrl}
                  className={`card-img doc-img border-end rounded-start ${getIsThumbInverted() ? 'inverted' : ''}`}
                  alt={document.title}
                />
              ) : (
                <div className="card-img doc-img border-end rounded-start bg-light d-flex align-items-center justify-content-center">
                  <div className="spinner-border spinner-border-sm" role="status"></div>
                </div>
              )}
              <div className="document-card-check">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`largeCardCheck${document.id}`}
                    checked={selected}
                    onChange={() => {}}
                    onClick={(e) => {
                      e.stopPropagation()
                      onToggleSelected(document, e)
                    }}
                  />
                  <label className="form-check-label" htmlFor={`largeCardCheck${document.id}`}></label>
                </div>
                {canDelete && (
                  <button type="button" className="document-card-delete" onClick={handleDeleteClick} title={t('delete_document')}>
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </div>
              {showVersionWatermark && (
                <div className="document-version-watermark">
                  <span className="badge rounded bg-light text-dark border">V{document.version_number ?? 0}</span>
                </div>
              )}
              {showStatusWatermark && document.workflow_status && (
                <div className="document-status-watermark">
                  <span
                    className="badge rounded"
                    style={getWorkflowStatusStyle(document.workflow_status)}
                  >
                    {getWorkflowStatusName(document.workflow_status)}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="placeholder bg-secondary w-100 card-img doc-img border-end rounded-start"></div>
          )}
        </div>

        <div className="col col-md-10">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title w-100">
                {document ? (
                  <>
                    {displayFields.includes(DisplayFieldEnum.TITLE) && <span>{document.title || document.original_file_name}</span>}
                    {displayFields.includes(DisplayFieldEnum.TAGS) && document.tags && document.tags.length > 0 && (
                      <>
                        {document.tags.map((tagId: number) => (
                          <Tag
                            key={tagId}
                            tag={getTagDisplayById(tagId)}
                            tagID={tagId}
                            linkTitle={t('filter_by_tag')}
                            className="ms-1"
                            onClick={onClickTag}
                            clickable={!!onClickTag}
                          />
                        ))}
                      </>
                    )}
                  </>
                ) : (
                  <div className="placeholder bg-secondary w-75 mb-3">&nbsp;</div>
                )}
              </h5>
            </div>

            <div className="card-text">
              {document ? (
                <div className="document-info d-flex flex-wrap align-items-center">
                  {displayFields.includes(DisplayFieldEnum.CORRESPONDENT) && document.correspondent && (
                    <div className="me-3 mb-1 d-flex align-items-center">
                      <span>{t('correspondent')}: </span>
                      {onClickCorrespondent ? (
                        <button
                          type="button"
                          title={t('filter_by_correspondent')}
                          onClick={(e) => {
                            e.stopPropagation()
                            onClickCorrespondent(typeof document.correspondent === 'object' ? document.correspondent.id : document.correspondent)
                          }}
                          className="btn btn-link p-0 border-0 text-decoration-none ms-1"
                          style={{ cursor: 'pointer', fontSize: 'inherit', fontWeight: 'inherit' }}
                        >
                          {getCorrespondentName(document.correspondent)}
                        </button>
                      ) : (
                        <span className="ms-1">{getCorrespondentName(document.correspondent)}</span>
                      )}
                    </div>
                  )}
                  {displayFields.includes(DisplayFieldEnum.DOCUMENT_TYPE) && document.document_type && (
                    <div className="me-3 mb-1 d-flex align-items-center">
                      <span>{t('document_type')}: </span>
                      {onClickDocumentType ? (
                        <button
                          type="button"
                          title={t('filter_by_document_type')}
                          onClick={(e) => {
                            e.stopPropagation()
                            onClickDocumentType(typeof document.document_type === 'object' ? document.document_type.id : document.document_type)
                          }}
                          className="btn btn-link p-0 border-0 text-decoration-none ms-1"
                          style={{ cursor: 'pointer', fontSize: 'inherit', fontWeight: 'inherit' }}
                        >
                          {getDocumentTypeName(document.document_type)}
                        </button>
                      ) : (
                        <span className="ms-1">{getDocumentTypeName(document.document_type)}</span>
                      )}
                    </div>
                  )}
                  {displayFields.includes(DisplayFieldEnum.STORAGE_PATH) && document.storage_path && (
                    <div className="me-3 mb-1 d-flex align-items-center">
                      <span>{t('storage_path')}: </span>
                      {onClickStoragePath ? (
                        <button
                          type="button"
                          title={t('filter_by_storage_path')}
                          onClick={(e) => {
                            e.stopPropagation()
                            onClickStoragePath(typeof document.storage_path === 'object' ? document.storage_path.id : document.storage_path)
                          }}
                          className="btn btn-link p-0 border-0 text-decoration-none ms-1"
                          style={{ cursor: 'pointer', fontSize: 'inherit', fontWeight: 'inherit' }}
                        >
                          {getStoragePathName(document.storage_path)}
                        </button>
                      ) : (
                        <span className="ms-1">{getStoragePathName(document.storage_path)}</span>
                      )}
                    </div>
                  )}
                  {displayFields.includes(DisplayFieldEnum.OWNER) &&
                    document.owner &&
                    (typeof document.owner === 'object' ? document.owner.id : document.owner) !== currentUserId && (
                      <div className="me-3 mb-1 d-flex align-items-center">
                        <span>{t('owner')}: </span>
                        <span className="ms-1">{getDocumentOwnerDisplayName(document, usersCache, usersCacheLoaded)}</span>
                      </div>
                    )}
                  {document.custom_fields &&
                    document.custom_fields.map((field: any) => {
                      const fieldKey = `${DisplayFieldEnum.CUSTOM_FIELD}${field.field || field.id}` as DisplayFieldEnum
                      return displayFields.includes(fieldKey) ? (
                        <div key={field.field || field.id} className="me-3 mb-1 d-flex align-items-center" title={field.field_display?.name}>
                          <i className="bi bi-ui-radios me-2 text-muted" style={{ width: '.9em', height: '.9em' }}></i>
                          <small>
                            {formatCustomFieldDisplayValue(field) ||
                              (field.field_display?.name ? (
                                <span className="fst-italic text-muted">{field.field_display.name}</span>
                              ) : (
                                ''
                              ))}
                          </small>
                        </div>
                      ) : null
                    })}
                </div>
              ) : (
                <>
                  <div className="placeholder bg-secondary w-100 d-block mb-2"></div>
                  <div className="placeholder bg-secondary w-75 d-block mb-2"></div>
                  <div className="placeholder bg-secondary w-25 d-block"></div>
                </>
              )}
            </div>

            <div className="d-flex flex-column flex-md-row align-items-md-center">
              <div className="btn-group">
                {document ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={(e) => {
                        e.stopPropagation()
                        onClickMoreLike(document.id)
                      }}
                    >
                      <i className="bi bi-diagram-3"></i>&nbsp;
                      <span className="d-none d-md-inline">{t('more_like_this')}</span>
                    </button>
                    <a href={`/documents/${document.id}`} className="btn btn-sm btn-outline-secondary" onClick={(e) => e.stopPropagation()}>
                      <i className="bi bi-file-earmark-richtext"></i>&nbsp;
                      <span className="d-none d-md-inline">{t('open')}</span>
                    </a>
                    <PreviewPopup document={document}>
                      <button className="btn btn-sm btn-outline-secondary" type="button">
                        <i className="bi bi-eye"></i>&nbsp;
                        <span className="d-none d-md-inline">{t('preview')}</span>
                      </button>
                    </PreviewPopup>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleDownload}>
                      <i className="bi bi-download"></i>&nbsp;
                      <span className="d-none d-md-inline">{t('download')}</span>
                    </button>
                  </>
                ) : (
                  <>
                    <div className="placeholder btn btn-sm btn-outline-secondary bg-secondary" style={{ width: '60px' }}>
                      &nbsp;
                    </div>
                    <div className="placeholder btn btn-sm btn-outline-secondary bg-secondary" style={{ width: '60px' }}>
                      &nbsp;
                    </div>
                    <div className="placeholder btn btn-sm btn-outline-secondary bg-secondary" style={{ width: '60px' }}>
                      &nbsp;
                    </div>
                    <div className="placeholder btn btn-sm btn-outline-secondary bg-secondary" style={{ width: '60px' }}>
                      &nbsp;
                    </div>
                  </>
                )}
              </div>

              <div className="list-group list-group-horizontal border-0 card-info ms-md-auto mt-2 mt-md-0">
                {document ? (
                  <>
                    {displayFields.includes(DisplayFieldEnum.NOTES) && document.notes && document.notes.length > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          window.location.href = `/documents/${document.id}/notes`
                        }}
                        className="list-group-item btn btn-sm bg-light text-dark p-1 border-0 me-2 d-flex align-items-center"
                        title={t('view_notes')}
                      >
                        <i className="bi bi-chat-left-text me-2 text-muted" style={{ width: '.9em', height: '.9em' }}></i>
                        <small>{document.notes.length} Notes</small>
                      </button>
                    )}
                    {displayFields.includes(DisplayFieldEnum.ASN) && document.archive_serial_number && (
                      <div className="list-group-item me-2 bg-light text-dark p-1 border-0 d-flex align-items-center">
                        <i className="bi bi-upc-scan me-2 text-muted" style={{ width: '.9em', height: '.9em' }}></i>
                        <small>#{document.archive_serial_number}</small>
                      </div>
                    )}
                    {(displayFields.includes(DisplayFieldEnum.CREATED) || displayFields.includes(DisplayFieldEnum.ADDED)) && (
                      <>
                        {displayFields.includes(DisplayFieldEnum.CREATED) && document.created && (
                          <div className="list-group-item bg-light text-dark p-1 border-0 d-flex align-items-center">
                            <i className="bi bi-calendar-event me-2 text-muted" style={{ width: '.9em', height: '.9em' }}></i>
                            <small>{formatDate(document.created)}</small>
                          </div>
                        )}
                        {displayFields.includes(DisplayFieldEnum.ADDED) && document.added && (
                          <div className="list-group-item bg-light text-dark p-1 border-0 d-flex align-items-center">
                            <i className="bi bi-calendar-event me-2 text-muted" style={{ width: '.9em', height: '.9em' }}></i>
                            <small>{formatDate(document.added)}</small>
                          </div>
                        )}
                      </>
                    )}
                    {displayFields.includes(DisplayFieldEnum.PAGE_COUNT) && document.page_count && (
                      <div className="list-group-item bg-light text-dark p-1 border-0 d-flex align-items-center">
                        <i className="bi bi-files me-2 text-muted" style={{ width: '.9em', height: '.9em' }}></i>
                        <small>
                          {document.page_count} {document.page_count === 1 ? t('page') : t('pages')}
                        </small>
                      </div>
                    )}
                    {document.__search_hit__?.score && (
                      <div className="list-group-item bg-light text-dark border-0 d-flex p-0 ps-4 search-score">
                        <small className="me-2 text-muted">Score:</small>
                        <div className="progress search-score-bar mx-2 mt-1" style={{ width: '100px', height: '5px' }}>
                          <div className="progress-bar bg-warning" style={{ width: `${document.__search_hit__.score * 100}%` }}></div>
                        </div>
                      </div>
                    )}
                    {displayFields.includes(DisplayFieldEnum.WORKFLOW_STATUS) && document.workflow_status && (
                      onClickWorkflowStatus ? (
                        <button
                          type="button"
                          className="list-group-item list-group-item-action bg-light text-dark p-1 border-0 me-2 d-flex align-items-center"
                          title={t('filter_by_workflow_status')}
                          onClick={(e) => {
                            e.stopPropagation()
                            const id = typeof document.workflow_status === 'object' ? (document.workflow_status as any).id : document.workflow_status
                            if (id != null) onClickWorkflowStatus(id)
                          }}
                        >
                          <i className="bi bi-flag-fill me-2 text-muted" style={{ width: '.9em', height: '.9em' }}></i>
                          <small>{getWorkflowStatusName(document.workflow_status)}</small>
                        </button>
                      ) : (
                        <div className="list-group-item bg-light text-dark p-1 border-0 d-flex align-items-center">
                          <i className="bi bi-flag-fill me-2 text-muted" style={{ width: '.9em', height: '.9em' }}></i>
                          <small>{getWorkflowStatusName(document.workflow_status)}</small>
                        </div>
                      )
                    )}
                  </>
                ) : (
                  <>
                    <div className="placeholder list-group-item bg-secondary w-25">&nbsp;</div>
                    <div className="placeholder list-group-item bg-secondary w-25">&nbsp;</div>
                    <div className="placeholder list-group-item bg-secondary w-25">&nbsp;</div>
                    <div className="placeholder list-group-item bg-secondary w-25">&nbsp;</div>
                    <div className="placeholder list-group-item bg-secondary w-25">&nbsp;</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        show={showDeleteDialog}
        title={t('confirm_delete')}
        message={
          t('confirm_move_to_trash', { count: 1 }) ||
          t('confirm_delete_document', { title: document?.title || document?.original_file_name || 'Untitled' })
        }
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (!isDeleteSubmitting) {
            setShowDeleteDialog(false)
          }
        }}
        variant="danger"
        showPermanentDeleteOption={true}
        permanentDeleteLabel={t('permanent_delete')}
        permanentDeleteDescription={t('permanent_delete_description')}
        isConfirming={isDeleteSubmitting}
      />
    </div>
  )
}

export default DocumentCardLarge
